// src/components/setting/ForgotPasswordDialog.jsx
// Drop-in dialog: identifier → OTP → new password → back to security tab

import React, { useState } from 'react';
import { toast } from 'sonner';
import { Phone, Mail, Check, ArrowLeft, RefreshCw, KeyRound } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import apiClient from '../../services/apiClient';

const api = {
  send:   (identifier, channel) =>
    apiClient.post('/api/auth/forgot-password/send',   { identifier, channel }).then(r => r.data),
  verify: (identifier, code) =>
    apiClient.post('/api/auth/forgot-password/verify', { identifier, code    }).then(r => r.data),
  reset:  (resetToken, newPassword) =>
    apiClient.post('/api/auth/forgot-password/reset',  { resetToken, newPassword }).then(r => r.data),
};

export default function ForgotPasswordDialog({ open, onClose }) {
  const [step,        setStep]       = useState('identifier'); // identifier | channel | otp | newpass | done
  const [identifier,  setIdentifier] = useState('');
  const [isEmail,     setIsEmail]    = useState(false);
  const [channel,     setChannel]    = useState('sms');
  const [otp,         setOtp]        = useState('');
  const [resetToken,  setResetToken] = useState('');
  const [newPass,     setNewPass]    = useState('');
  const [confirmPass, setConfirmPass]= useState('');
  const [loading,     setLoading]    = useState(false);
  const [maskedTo,    setMaskedTo]   = useState('');
  const [countdown,   setCountdown]  = useState(0);

  const startCountdown = () => {
    setCountdown(30);
    const t = setInterval(() => {
      setCountdown(c => { if (c <= 1) { clearInterval(t); return 0; } return c - 1; });
    }, 1000);
  };

  const reset = () => {
    setStep('identifier'); setIdentifier(''); setIsEmail(false);
    setChannel('sms'); setOtp(''); setResetToken('');
    setNewPass(''); setConfirmPass(''); setMaskedTo(''); setCountdown(0);
  };

  // Step 1 — parse identifier type
  const handleIdentifierNext = () => {
    const trimmed = identifier.trim();
    if (!trimmed) { toast.error('Enter your email or phone number'); return; }
    const emailMode = trimmed.includes('@');
    setIsEmail(emailMode);
    if (emailMode) {
      // email — no channel choice needed, go straight to send
      handleSend(trimmed, 'email');
    } else {
      // phone — choose SMS or WhatsApp
      setStep('channel');
    }
  };

  // Step 2 (phone only) — choose channel
  const handleChannelNext = () => handleSend(identifier.trim(), channel);

  // Send OTP
  const handleSend = async (id, ch) => {
    setLoading(true);
    try {
      const res = await api.send(id, ch);
      setMaskedTo(res.data?.to || id);
      setStep('otp');
      startCountdown();
      toast.success(`OTP sent to ${res.data?.to || id}`);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to send OTP');
    }
    setLoading(false);
  };

  // Step 3 — verify OTP
  const handleVerifyOTP = async () => {
    if (otp.length < 4) { toast.error('Enter the OTP'); return; }
    setLoading(true);
    try {
      const res = await api.verify(identifier.trim(), otp);
      setResetToken(res.data?.resetToken);
      setStep('newpass');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Invalid or expired OTP');
    }
    setLoading(false);
  };

  // Step 4 — reset password
  const handleReset = async () => {
    if (newPass.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (newPass !== confirmPass) { toast.error('Passwords do not match'); return; }
    setLoading(true);
    try {
      await api.reset(resetToken, newPass);
      setStep('done');
      toast.success('Password reset successfully!');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to reset password');
    }
    setLoading(false);
  };

  const handleClose = () => { reset(); onClose(); };

  const stepTitles = {
    identifier: 'Forgot Password',
    channel:    'Choose Delivery Method',
    otp:        'Enter Verification Code',
    newpass:    'Set New Password',
    done:       'Password Reset!',
  };

  const stepDesc = {
    identifier: 'Enter the email address or phone number you registered with.',
    channel:    'How would you like to receive the verification code?',
    otp:        `We sent a 6-digit code to ${maskedTo}.`,
    newpass:    'Create a strong new password for your account.',
    done:       'Your password has been updated. You can now log in with your new password.',
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <KeyRound className="w-5 h-5 text-[#2E6DA4]" />
            {stepTitles[step]}
          </DialogTitle>
          <DialogDescription>{stepDesc[step]}</DialogDescription>
        </DialogHeader>

        {/* ── Step: identifier ── */}
        {step === 'identifier' && (
          <div className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Email or Phone</Label>
              <Input
                value={identifier}
                onChange={e => setIdentifier(e.target.value)}
                placeholder="you@example.com or 9876543210"
                autoFocus
                onKeyDown={e => e.key === 'Enter' && handleIdentifierNext()}
              />
            </div>
          </div>
        )}

        {/* ── Step: channel (phone only) ── */}
        {step === 'channel' && (
          <div className="space-y-4 pt-1">
            <div className="grid grid-cols-2 gap-2">
              {[
                { value: 'sms',      label: '💬 SMS'      },
                { value: 'whatsapp', label: '📱 WhatsApp' },
              ].map(ch => (
                <button key={ch.value} onClick={() => setChannel(ch.value)}
                  className={['py-3 rounded-xl text-sm font-semibold border-2 transition-all',
                    channel === ch.value
                      ? 'bg-[#2E6DA4] text-white border-transparent'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-[#2E6DA4]',
                  ].join(' ')}>
                  {ch.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* ── Step: OTP ── */}
        {step === 'otp' && (
          <div className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">6-digit code</Label>
              <Input
                value={otp} onChange={e => setOtp(e.target.value.replace(/\D/g,'').slice(0,6))}
                placeholder="••••••"
                className="text-center text-2xl font-mono tracking-[0.5em] h-12"
                maxLength={6} autoFocus
                onKeyDown={e => e.key === 'Enter' && handleVerifyOTP()}
              />
            </div>
            <div className="flex items-center justify-between text-xs text-slate-400">
              <span>Didn't receive it?</span>
              {countdown > 0 ? (
                <span>Resend in {countdown}s</span>
              ) : (
                <button onClick={() => handleSend(identifier.trim(), channel)}
                  className="text-[#2E6DA4] hover:underline">
                  Resend
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Step: new password ── */}
        {step === 'newpass' && (
          <div className="space-y-4 pt-1">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">New Password</Label>
              <Input type="password" value={newPass}
                onChange={e => setNewPass(e.target.value)}
                placeholder="Min 8 characters" autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-slate-600">Confirm Password</Label>
              <Input type="password" value={confirmPass}
                onChange={e => setConfirmPass(e.target.value)}
                placeholder="Repeat new password"
                onKeyDown={e => e.key === 'Enter' && handleReset()} />
            </div>
            {newPass && confirmPass && newPass !== confirmPass && (
              <p className="text-xs text-red-500">Passwords don't match</p>
            )}
          </div>
        )}

        {/* ── Step: done ── */}
        {step === 'done' && (
          <div className="py-4 text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <p className="text-sm text-slate-600">Your password has been reset successfully.</p>
          </div>
        )}

        <DialogFooter className="gap-2 flex-col sm:flex-row">
          {step !== 'done' && (
            <Button variant="outline" onClick={step === 'identifier' ? handleClose : () => {
              if (step === 'channel') setStep('identifier');
              else if (step === 'otp') setStep(isEmail ? 'identifier' : 'channel');
              else if (step === 'newpass') setStep('otp');
            }} className="gap-1">
              <ArrowLeft className="w-3.5 h-3.5" />
              {step === 'identifier' ? 'Cancel' : 'Back'}
            </Button>
          )}

          {step === 'identifier' && (
            <Button onClick={handleIdentifierNext} disabled={loading}
              className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Continue →'}
            </Button>
          )}
          {step === 'channel' && (
            <Button onClick={handleChannelNext} disabled={loading}
              className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Send Code →'}
            </Button>
          )}
          {step === 'otp' && (
            <Button onClick={handleVerifyOTP} disabled={loading || otp.length < 6}
              className="bg-[#2E6DA4] hover:bg-[#255a88] text-white">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Verify →'}
            </Button>
          )}
          {step === 'newpass' && (
            <Button onClick={handleReset}
              disabled={loading || newPass.length < 8 || newPass !== confirmPass}
              className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {loading ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Reset Password'}
            </Button>
          )}
          {step === 'done' && (
            <Button onClick={handleClose} className="bg-[#2E6DA4] hover:bg-[#255a88] text-white w-full">
              Back to Settings
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}